import React, { useState } from "react";
import Model, { helper } from "@tripian/model";
import {
  CustomPopover,
  FavoriteIconButton2,
  StepTimes,
} from "@tripian/react";

interface PoiCardProps {
  poi: Model.Poi;
  inStep: boolean;
  isFavorite: boolean;
  isLoadingFavorite: boolean;
  dayNumbers?: number[];
  TOUR_PROVIDER_IDS: Model.PROVIDER_ID[];
  TICKET_PROVIDER_IDS: Model.PROVIDER_ID[];
  RESTAURANT_RESERVATION_PROVIDER_IDS: Model.PROVIDER_ID[];
  hideTourTicketIcons: boolean;
  hideActionButtons?: boolean;
  skipStepTimesModal?: boolean;
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  tourTicketProductsLoading?: boolean;
  poiCardClicked: (poi: Model.Poi) => void;
  onFavoriteToggle: (poi: Model.Poi, add: boolean) => void;
  onAddPoiClick: (poi: Model.Poi, from?: string, to?: string) => void;
  onBookingClick: (poi: Model.Poi) => void;
  t: (value: Model.TranslationKey) => string;
}

const PoiCard: React.FC<PoiCardProps> = ({
  poi,
  inStep = false,
  isFavorite,
  isLoadingFavorite,
  dayNumbers,
  TOUR_PROVIDER_IDS,
  TICKET_PROVIDER_IDS,
  RESTAURANT_RESERVATION_PROVIDER_IDS,
  hideTourTicketIcons = false,
  hideActionButtons = false,
  skipStepTimesModal = false,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  poiCardClicked,
  onFavoriteToggle,
  onAddPoiClick,
  onBookingClick,
  t,
}) => {
  const [show, setShow] = useState(false);

  // Slider için eklenen state - her POI için ayrı index tutar
  const [activeSlides, setActiveSlides] = useState<Record<string, number>>({});

  // Resim yükleniyor durumunu takip etmek için state
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>(
    {}
  );

  // POI resimleri için dizi oluşturuyoruz
  const poiGallery = poi.gallery || [];
  const mainImage = poi.image?.url
    ? { url: poi.image.url, width: poi.image.width, height: poi.image.height }
    : null;

  // Ana resmin URL'si ile galerinin ilk resminin URL'si aynıysa, galerinin ilk elemanını atlayalım
  const filteredGallery =
    poiGallery.length > 0 && mainImage && poiGallery[0].url === mainImage.url
      ? poiGallery.slice(1)
      : poiGallery;

  // Ana resmi ve filtre edilmiş galeri resimlerini birleştiriyoruz
  const allImages = mainImage
    ? [mainImage, ...filteredGallery]
    : filteredGallery;

  // Bu POI için aktif slider indeksi
  const activeSlideIndex = activeSlides[poi.id] || 0;

  // Aktif sliderda gösterilen resim
  const currentImageUrl =
    allImages.length > 0
      ? helper.imgUrl(allImages[activeSlideIndex].url || "", 400, 400)
      : "";

  const cuisinesArray = poi.cuisines?.split(", ") || [];
  const uniqueCuisines = helper.removeDuplicateValues(
    cuisinesArray,
    (s1, s2) => s1 === s2
  );

  // Slider için yardımcı fonksiyonlar
  const nextSlide = (
    e: React.MouseEvent,
    poiId: string,
    imagesLength: number
  ) => {
    e.stopPropagation();
    // Resmin yüklendiğini takip etmek için loading durumunu true yapıyoruz
    setLoadingImages(prev => ({ ...prev, [poiId]: true }));
    setActiveSlides(prev => {
      const currentIndex = prev[poiId] || 0;
      return {
        ...prev,
        [poiId]: currentIndex === imagesLength - 1 ? 0 : currentIndex + 1,
      };
    });

    // Resim yüklenmediğinde loading durumunu otomatik olarak kapatmak için timeout ekleyelim
    setTimeout(() => {
      setLoadingImages(prev => ({ ...prev, [poiId]: false }));
    }, 1000);
  };

  const prevSlide = (
    e: React.MouseEvent,
    poiId: string,
    imagesLength: number
  ) => {
    e.stopPropagation();
    // Resmin yüklendiğini takip etmek için loading durumunu true yapıyoruz
    setLoadingImages(prev => ({ ...prev, [poiId]: true }));
    setActiveSlides(prev => {
      const currentIndex = prev[poiId] || 0;
      return {
        ...prev,
        [poiId]: currentIndex === 0 ? imagesLength - 1 : currentIndex - 1,
      };
    });

    // Resim yüklenmediğinde loading durumunu otomatik olarak kapatmak için timeout ekleyelim
    setTimeout(() => {
      setLoadingImages(prev => ({ ...prev, [poiId]: false }));
    }, 1000);
  };

  const goToSlide = (e: React.MouseEvent, poiId: string, index: number) => {
    e.stopPropagation();
    // Resmin yüklendiğini takip etmek için loading durumunu true yapıyoruz
    setLoadingImages(prev => ({ ...prev, [poiId]: true }));
    setActiveSlides(prev => ({
      ...prev,
      [poiId]: index,
    }));

    // Resim yüklenmediğinde loading durumunu otomatik olarak kapatmak için timeout ekleyelim
    setTimeout(() => {
      setLoadingImages(prev => ({ ...prev, [poiId]: false }));
    }, 1000);
  };

  // Resmin yüklenme durumunu işleyen fonksiyon
  const handleImageLoad = (poiId: string) => {
    setLoadingImages(prev => ({ ...prev, [poiId]: false }));
  };

  const reservationAvailable = helper.restaurantReservationAvailable(
    poi.bookings,
    RESTAURANT_RESERVATION_PROVIDER_IDS
  );

  return (
    <div
      key={`search-result-poi-${poi.id}`}
      className="group mb-4 flex flex-row overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-md cursor-pointer"
      onClick={() => poiCardClicked(poi)}
    >
      {/* Sol taraf - Slider ve favori ikonu */}
      <div className="relative h-[120px] sm:h-[130px] md:h-[140px] lg:h-[180px] w-[120px] sm:w-[140px] md:w-[180px] lg:w-[260px] flex-shrink-0 overflow-hidden">
        {/* Resim slider */}
        <div className="w-full h-full relative">
          {allImages.length > 0 ? (
            <div className="relative w-full h-full">
              <img
                src={currentImageUrl}
                alt={poi.name}
                className={`h-full w-full object-cover transition-all duration-300 ease-in-out rounded-r-2xl ${loadingImages[poi.id] ? "opacity-40" : "opacity-100"}`}
                loading="lazy"
                onLoad={() => handleImageLoad(poi.id)}
              />

              {/* Resim yüklenirken loader göster */}
              {loadingImages[poi.id] && (
                <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/10">
                  <div className="w-10 h-10 border-[3px] border-white border-t-transparent rounded-full animate-spin shadow-md"></div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full w-full bg-gray-200 flex items-center justify-center rounded-l-2xl">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}

          {/* Slider navigasyon okları - birden fazla resim varsa göster */}
          {allImages.length > 1 && (
            <>
              {/* Sol ok */}
              <button
                onClick={e => prevSlide(e, poi.id, allImages.length)}
                className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all"
              >
                <svg
                  className="w-4 h-4 text-gray-700"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Sağ ok */}
              <button
                onClick={e => nextSlide(e, poi.id, allImages.length)}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white bg-opacity-70 shadow-sm flex items-center justify-center z-10 hover:bg-opacity-90 transition-all"
              >
                <svg
                  className="w-4 h-4 text-gray-700"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          )}

          {/* Slider noktaları göstergesi */}
          {allImages.length > 1 && (
            <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1 z-10">
              {allImages.map((_, index) => (
                <button
                  key={`slider-dot-${poi.id}-${index}`}
                  onClick={e => goToSlide(e, poi.id, index)}
                  className={`w-1.5 h-1.5 rounded-full ${index === activeSlideIndex ? "bg-white" : "bg-white bg-opacity-50"}`}
                  aria-label={`${t("trips.myTrips.itinerary.slider.goToSlide")} ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Favori ikonu */}
        <FavoriteIconButton2
          className="absolute right-2 top-2"
          favorite={isFavorite}
          loading={isLoadingFavorite}
          clicked={() => onFavoriteToggle(poi, !isFavorite)}
        />
      </div>

      {/* Sağ taraf - İçerik */}
      <div className="flex flex-1 flex-col py-2 px-3 lg:py-3 lg:px-4 justify-between overflow-hidden">
        <div>
          <div className="flex items-center justify-between mb-[0.125rem] lg:mb-1">
            {/* Puan bilgisi */}
            {poi.rating && poi.rating > 0 && (
              <div className="flex items-center">
                <span className="text-xs sm:text-sm font-medium mr-1">
                  {poi.rating ? poi.rating.toFixed(1) : ""}
                </span>
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
                {poi.ratingCount && poi.ratingCount > 0 && (
                  <span className="text-xs text-gray-500 ml-1 hidden sm:inline">
                    ({poi.ratingCount})
                  </span>
                )}
              </div>
            )}

            {/* Gün bilgisi veya Ekle butonu - sağ üstte */}
            <div className="flex items-center gap-1">
              {dayNumbers && dayNumbers.length > 0 && (
                <div className="flex items-center">
                  <span className="inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 text-xs sm:text-sm font-medium rounded-full border border-green-500 bg-white text-green-600 min-w-[60px] sm:min-w-[80px]">
                    {t("trips.myTrips.itinerary.day")} {dayNumbers.join(",")}
                  </span>
                </div>
              )}

              {(!dayNumbers || dayNumbers.length === 0) &&
                !inStep &&
                !hideActionButtons && (
                  <div>
                    {skipStepTimesModal ? (
                      <button
                        key={`step-button-${poi.id}`}
                        className="inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 rounded-full border border-gray-300 bg-white text-primary-color hover:bg-primary-color hover:text-white transition-colors min-w-[60px] sm:min-w-[80px]"
                        onClick={e => {
                          e.stopPropagation();
                          onAddPoiClick(poi);
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          className="sm:w-4 sm:h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 5V19M5 12H19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    ) : (
                      <CustomPopover
                        show={show}
                        content={
                          <StepTimes
                            times={{
                              from: undefined,
                              to: undefined,
                            }}
                            timesClicked={(from?: string, to?: string) => {
                              onAddPoiClick(poi, from, to);
                              setShow(false);
                            }}
                            buttonText={t(
                              "trips.myTrips.itinerary.step.addToItinerary.submit.apply"
                            )}
                            duration={poi.duration}
                            onClose={() => setShow(false)}
                            t={t}
                          />
                        }
                        backdropClick={e => {
                          e.stopPropagation();
                          if (show) setShow(false);
                        }}
                      >
                        <button
                          key={`step-button-${poi.id}`}
                          className="inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 rounded-full border border-gray-300 bg-white text-primary-color hover:bg-primary-color hover:text-white transition-colors min-w-[60px] sm:min-w-[80px]"
                          onClick={e => {
                            e.stopPropagation();
                            setShow(prevShow => !prevShow);
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            className="sm:w-4 sm:h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 5V19M5 12H19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </CustomPopover>
                    )}
                  </div>
                )}
              {/* Eğer booking veya tour varsa booking etiketi göster */}
              {!hideTourTicketIcons &&
                (poi.hasBookings || reservationAvailable) && (
                  <span
                    onClick={() => {
                      if (reservationAvailable) {
                        poiCardClicked(poi);
                      } else {
                        onBookingClick(poi);
                      }
                    }}
                    className="inline-flex items-center justify-center h-6 sm:h-8 px-2 sm:px-4 text-xs sm:text-sm font-medium rounded-full border border-primary-color bg-primary-color text-white min-w-[60px] sm:min-w-[80px]"
                  >
                    Book
                  </span>
                )}
            </div>
          </div>

          {/* İsim */}
          <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-[0.125rem] lg:mb-1 line-clamp-2">
            {poi.name}
            {(window as any).tconfig?.ELIGIBILITY_TYPE === "visa" &&
              poi.externalUrl && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    window.open(poi.externalUrl!, "_blank", "noopener,noreferrer");
                  }}
                  className="inline-flex items-center justify-center ml-1 align-middle"
                  aria-label="External link"
                >
                  <img
                    src={(window as any).tconfig?.IMAGE_PATHS?.LOGO_PATH_LIGHT}
                    alt="External link"
                    className="h-[0.6rem] w-auto object-contain hover:opacity-80 transition-opacity"
                  />
                </button>
              )}
          </h3>

          {/* Açıklama (varsa) - tablet ve üzerinde göster */}
          {poi.description && (
            <p
              className={`text-xs sm:text-sm text-gray-600 mb-[0.125rem] lg:mb-1 [display:-webkit-box] [-webkit-box-orient:vertical] overflow-hidden ${
                dayNumbers && dayNumbers.length > 0
                  ? "line-clamp-2"
                  : "line-clamp-2"
              }`}
            >
              {poi.description}
            </p>
          )}

          {uniqueCuisines.length > 0 && (
            <p className="text-xs sm:text-sm text-gray-600 [display:-webkit-box] [-webkit-box-orient:vertical] overflow-hidden line-clamp-1">
              {uniqueCuisines.join(", ")}
            </p>
          )}
        </div>

        {/* İkonlar - daha kompakt dizayn */}
        {/* <div className="flex items-center gap-1 mt-auto flex-wrap"> */}
        {/* İkonlar bölümü şimdilik gizlendi
          {toursLoading && poi.category.some((c) => c.id === 1) ? (
            <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-100">
              <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full border-2 border-primary-color border-t-transparent animate-spin"></div>
            </div>
          ) : (
            <>
              {tourAvailable && (
                <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-color shadow-sm">
                  <svg width="12" height="12" className="sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V18C23 18.5304 22.7893 19.0391 22.4142 19.4142C22.0391 19.7893 21.5304 20 21 20H3C2.46957 20 1.96086 19.7893 1.58579 19.4142C1.21071 19.0391 1 18.5304 1 18V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3Z" stroke="white" strokeWidth="1.5" fill="none"/>
                    <circle cx="12" cy="13" r="4" stroke="white" strokeWidth="1.5" fill="none"/>
                  </svg>
                </div>
              )}
              {ticketAvailable && (
                <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-color shadow-sm">
                  <svg width="12" height="12" className="sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 10V6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V10C3.1 10 4 10.9 4 12C4 13.1 3.1 14 2 14V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V14C20.9 14 20 13.1 20 12C20 10.9 20.9 10 22 10Z" stroke="white" strokeWidth="2" fill="none"/>
                    <path d="M11 15H13V17H11V15ZM11 11H13V13H11V11ZM11 7H13V9H11V7Z" fill="white"/>
                  </svg>
                </div>
              )}
              {reservationAvailable && (
                <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-color shadow-sm">
                  <svg width="12" height="12" className="sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.1 13.34L10.93 10.51L3.91 3.5C2.35 5.06 2.35 7.59 3.91 9.16L8.1 13.34Z" fill="white"/>
                    <path d="M14.88 11.53C16.41 12.24 18.56 11.74 20.15 10.15C22.06 8.24 22.43 5.5 20.96 4.03C19.5 2.57 16.76 2.93 14.84 4.84C13.25 6.43 12.75 8.58 13.46 10.11L3.7 19.87L5.11 21.28L12 14.41L18.88 21.29L20.29 19.88L13.41 13L14.88 11.53Z" fill="white"/>
                  </svg>
                </div>
              )}
              {offerAvailable && (
                <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-color shadow-sm">
                  <svg width="12" height="12" className="sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.41 11.58L12.41 2.58C12.05 2.22 11.55 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.55 2.22 12.05 2.59 12.42L11.59 21.42C11.95 21.78 12.45 22 13 22C13.55 22 14.05 21.78 14.41 21.41L21.41 14.41C21.78 14.05 22 13.55 22 13C22 12.45 21.77 11.94 21.41 11.58Z" stroke="white" strokeWidth="2" fill="none"/>
                    <path d="M6.5 8C7.32843 8 8 7.32843 8 6.5C8 5.67157 7.32843 5 6.5 5C5.67157 5 5 5.67157 5 6.5C5 7.32843 5.67157 8 6.5 8Z" fill="white"/>
                  </svg>
                </div>
              )}
              {hasMustTries && (
                <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-color shadow-sm">
                  <svg width="12" height="12" className="sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="white"/>
                  </svg>
                </div>
              )}
            </>
          )}
          */}
        {/* </div> */}
      </div>
    </div>
  );
};

export default PoiCard;
